import React from 'react'
import {
  FRESH_CONFIG, FilterConfig, FILTER_CONFIG_SCHEMA, FilterLabels, OPERATORS, DataCategory,
} from './filterplusplus-model'
import { SparqlEditor } from '../../SPARQLEditor'
import { Input } from '../../ui-primitives/input'


const LABEL_FIELDS: Array<{ key: keyof FilterLabels; label: string; placeholder: string }> = [
  { key: 'andLabel',             label: 'AND connector',        placeholder: 'e.g. "and"' },
  { key: 'orLabel',              label: 'OR connector',         placeholder: 'e.g. "or"' },
  { key: 'addGroupLabel',        label: 'add group button',     placeholder: 'e.g. "add group"' },
  { key: 'addConditionLabel',    label: 'add condition button', placeholder: 'e.g. "add condition"' },
  { key: 'removeConditionLabel', label: 'remove condition',     placeholder: 'e.g. "remove"' },
  { key: 'removeGroupLabel',     label: 'remove group',         placeholder: 'e.g. "remove"' },
  { key: 'applyLabel',           label: 'apply button',         placeholder: 'e.g. "apply filter"' },
]

export function FilterPlusPlusConfig(props: {
  customConfig: FilterConfig | undefined
  onChange: (value: FilterConfig) => void
}): React.ReactNode {
  const [config, setConfig] = React.useState<FilterConfig>(() => {
    const result = FILTER_CONFIG_SCHEMA.safeParse(props.customConfig)
    return result.success ? result.data : FRESH_CONFIG
  })

  const [openCategory, setOpenCategory] = React.useState<DataCategory | null>(null)

  const setLabel = (key: keyof FilterLabels, value: string) => {
    setConfig((c) => ({
      ...c,
      labels: { ...c.labels, [key]: value || undefined },
    }))
  }

  React.useEffect(() => { props.onChange(config) }, [config])

  return (
    <div className="flex flex-col gap-4">

      <div className="bg-white border border-stone-200 border-l-[3px] border-l-blue-600 rounded-sm p-4">
        <div className="mono text-[10px] tracking-widest uppercase mb-3">
          <details>
            <summary className="text-stone-700 hover:text-orange-700">
              Construct query to setup filter targets. It should have the following structure (click to expand)
            </summary>
            <pre>
{`
PREFIX filterpp: <https://ont.wistor.nl/2026-04-29/widget/filterpp#>
PREFIX skos: <http://www.w3.org/2004/02/skos/core#>
PREFIX sh: <http://www.w3.org/ns/shacl#>
PREFIX xsd: <http://www.w3.org/2001/XMLSchema#>
PREFIX ex: <http://example.org/>

CONSTRUCT {
  ?propUri a filterpp:FilterOption ;

    filterpp:predicate ?predicate ;
    # should be an xsd:string, examples:
    # - \`<http://example.org/my-predicate>\` note the brackets!
    # - \`ex:rel-to-blanknode/ex:value\` make sure the PREFIX is not missing in consuming query

    skos:prefLabel ?label ;
    sh:datatype ?datatype ;
    sh:value ?picklistItem .

  ?picklistItem skos:prefLabel ?picklistLabel .
}
WHERE { ... }
`}
            </pre>
          </details>
        </div>
        <SparqlEditor
          id="filterpp-config-construct"
          mode="init-value"
          value={config.dynamicFilterTargetsConstructQuery}
          onBlur={(constructQuery) => setConfig((c) => ({...c, dynamicFilterTargetsConstructQuery: constructQuery}))}
        />
      </div>

      <div>
        <label>
          <input className="mr-2"
            type="checkbox"
            checked={Boolean(config.stringOperatorsCaseInsensitive)}
            onChange={e => setConfig(s => ({...s, stringOperatorsCaseInsensitive: e.target.checked}))}
          />
          case insensitive for strings
        </label>
      </div>

      <div className="bg-white border border-stone-200 border-l-[3px] border-l-orange-700 rounded-sm p-4 text-black">
        <div className="mono text-[10px] tracking-widest uppercase mb-3">
          labels
        </div>
        <div className="flex flex-col gap-2">
          {LABEL_FIELDS.map((field) => (
            <div key={field.key} className="flex items-center gap-2">
              <label className="mono text-[11px] text-stone-500 whitespace-nowrap w-48 shrink-0">
                {field.label}
              </label>
              <Input
                placeholder={field.placeholder}
                value={config.labels?.[field.key] ?? ''}
                onChange={(e) => setLabel(field.key, e.target.value)}
              />
            </div>
          ))}
        </div>
        <div className="mono text-[10px] tracking-widest uppercase mb-3">
          operator labels
        </div>
        <div className="flex flex-col gap-1">
          {(Object.keys(OPERATORS) as DataCategory[]).map((cat) => (
            <div key={cat} className="border border-stone-200 rounded-sm">
              <button
                className="w-full flex items-center justify-between px-3 py-2 mono text-[11px] text-stone-700 hover:text-orange-700 transition-colors"
                onClick={() => setOpenCategory(openCategory === cat ? null : cat)}
              >
                <span>operator: {cat}</span>
                <span className="text-xl">{openCategory === cat ? '▴' : '▾'}</span>
              </button>
              {openCategory === cat && (
                <div className="flex flex-col gap-1.5 px-3 pb-3 pt-1 border-t border-stone-100">
                  {Object.entries(OPERATORS[cat]).map(([op, defaultLabel]) => (
                    <div key={op} className="flex items-center gap-2">
                      <span className="mono text-[11px] w-32 shrink-0">
                        {defaultLabel}
                      </span>
                      <Input
                        placeholder={`custom label (default: "${defaultLabel}")`}
                        value={config.opLabels?.[cat]?.[op] ?? ''}
                        onChange={(e) => {
                          const val = e.target.value
                          setConfig((c) => {
                            const catLabels = { ...c.opLabels?.[cat] }
                            if (val) catLabels[op] = val
                            else delete catLabels[op]
                            return { ...c, opLabels: { ...c.opLabels, [cat]: catLabels } }
                          })
                        }}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

    </div>
  )
}
